import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
  TablePagination,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import DeleteRegionModal from "./M_DeleteRegion";
import RestoreRegionModal from "./M_RestoreRegion";
import ViewRegionModal from "./M_ViewRegion";
import TestRegionModal from "./M_TestRegion";
import RegionModal from "./M_Region";

import useRegion from "../../../../hooks/super/useRegion";
import useAuth from "../../../../hooks/useAuth";
import { formatDate } from "../../../../utils/format";
import { UserType } from "../../../../utils/constants";
import { getComparator, stableSort } from "../../../../utils/tableSort";

import {
  TOKENS,
  StatTile,
  StatusPill,
  Monogram,
  RowMenu,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "name", label: "Region", sortable: true },
  { id: "host_name", label: "Host", sortable: true },
  { id: "edge_ip", label: "Edge Private IP", sortable: true },
  { id: "res_code", label: "Response", sortable: true },
  { id: "isActive", label: "Reachable", sortable: true },
  { id: "isDeleted", label: "Status", sortable: true },
  { id: "created_at", label: "Created", sortable: true },
  { id: "action", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{ color: `${TOKENS.fg2} !important`, "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` } }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

const IdMono = ({ value }) => (
  <Typography
    sx={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 11,
      color: TOKENS.fg3,
    }}
  >
    {value}
  </Typography>
);

const ReachableDot = ({ value }) =>
  value ? (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: TOKENS.successInk }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.successInk, fontWeight: 500 }}>Online</Typography>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <CancelOutlinedIcon sx={{ fontSize: 16, color: TOKENS.dangerInk }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.dangerInk, fontWeight: 500 }}>Offline</Typography>
    </Stack>
  );

function RegionTable() {
  const { regions, total, from, size, getRegions, viewRegion } = useRegion();
  const { adminUserType } = useAuth();

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(size || 5);

  React.useEffect(() => {
    if (typeof size === "number") setRowsPerPage(size);
  }, [size]);
  React.useEffect(() => {
    if (typeof from === "number" && typeof size === "number" && size > 0) setPage(Math.floor(from / size));
  }, [from, size]);

  const handleSort = (id) => {
    if (id === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  // modal state
  const [editOpen, setEditOpen] = React.useState(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [testOpen, setTestOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [removeFlag, setRemoveFlag] = React.useState(false);

  const [regionID, setRegionID] = React.useState();
  const [regionName, setRegionName] = React.useState();
  const [region, setRegion] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const onView = async (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setLoading(true);
    setRegion(await viewRegion(row.id));
    setViewOpen(true);
    setLoading(false);
  };
  const onEdit = async (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setLoading(true);
    setRegion(await viewRegion(row.id));
    setEditOpen(true);
    setLoading(false);
  };
  const onTest = async (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setLoading(true);
    setRegion(await viewRegion(row.id));
    setTestOpen(true);
    setLoading(false);
  };
  const onDelete = (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setRemoveFlag(false);
    setDeleteOpen(true);
  };
  const onRemove = (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setRemoveFlag(true);
    setDeleteOpen(true);
  };
  const onRestore = (row) => {
    setRegionID(row.id);
    setRegionName(row.name);
    setRestoreOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getRegions(rowsPerPage, rowsPerPage * newPage, false);
  };
  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    getRegions(next, 0, false);
  };

  // counts
  const counts = React.useMemo(() => {
    const list = Array.isArray(regions) ? regions : [];
    let active = 0;
    let online = 0;
    let removed = 0;
    list.forEach((r) => {
      if (r?.isDeleted) removed += 1;
      else active += 1;
      if (r?.isActive) online += 1;
    });
    return { total: total || list.length, active, online, removed };
  }, [regions, total]);

  // search
  const filtered = React.useMemo(() => {
    const list = Array.isArray(regions) ? regions : [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (r) =>
        (r?.name || "").toLowerCase().includes(q) ||
        (r?.host_name || "").toLowerCase().includes(q) ||
        (r?.edge_ip || "").toLowerCase().includes(q) ||
        String(r?.id || "").toLowerCase().includes(q)
    );
  }, [regions, search]);

  const sorted = React.useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);

  const isLoading = regions === null;
  const isSuper = [UserType.SUPER_ADMIN].includes(adminUserType);

  return (
    <Box>
      {/* Summary tiles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile
          icon={<PublicIcon />}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.total}
          label="Total regions"
        />
        <StatTile
          icon={<CheckCircleOutlineIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.active}
          label="Active"
        />
        <StatTile
          icon={<TroubleshootIcon />}
          iconBg={TOKENS.infoBg}
          iconInk={TOKENS.infoInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.online}
          label="Reachable"
        />
        <StatTile
          icon={<CancelOutlinedIcon />}
          iconBg={TOKENS.dangerBg}
          iconInk={TOKENS.dangerInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.removed}
          label="Suspended"
        />
      </Box>

      {/* Main panel */}
      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            p: "14px 16px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by name, host, IP, id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3 }}>
            {isLoading ? "Loading..." : `${sorted.length} of ${counts.total}`}
          </Typography>
        </Box>

        <TableContainer>
          <Table aria-label="regions table" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                {headCells.map((c) => (
                  <HeaderCell
                    key={c.id}
                    id={c.id}
                    sortable={c.sortable}
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align={c.id === "action" ? "right" : "left"}
                  >
                    {c.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {headCells.map((__, j) => (
                        <DataCell key={`sk-${i}-${j}`}>
                          <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                        </DataCell>
                      ))}
                    </TableRow>
                  ))
                : sorted.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                        <EmptyState
                          icon={<PublicIcon />}
                          title={search ? "No regions match your search" : "No regions yet"}
                          body={search ? "Try a different keyword." : "Use the Add Region button to add one."}
                        />
                      </TableCell>
                    </TableRow>
                  )
                : sorted.map((row) => {
                    const name = row?.name || "—";
                    const actions = [
                      {
                        key: "view",
                        icon: <RemoveRedEyeIcon fontSize="small" />,
                        label: "View",
                        onClick: () => onView(row),
                      },
                      {
                        key: "test",
                        icon: <TroubleshootIcon fontSize="small" />,
                        label: "Test",
                        onClick: () => onTest(row),
                      },
                    ];
                    if (isSuper) {
                      actions.push({
                        key: "edit",
                        icon: <EditIcon fontSize="small" />,
                        label: "Edit",
                        onClick: () => onEdit(row),
                      });
                      if (row?.isDeleted) {
                        actions.push({
                          key: "restore",
                          icon: <RestoreIcon fontSize="small" />,
                          label: "Restore",
                          onClick: () => onRestore(row),
                        });
                      } else {
                        actions.push({
                          key: "delete",
                          icon: <DeleteIcon fontSize="small" />,
                          label: "Delete",
                          danger: true,
                          onClick: () => onDelete(row),
                        });
                      }
                      actions.push({ key: "div", divider: true });
                      actions.push({
                        key: "remove",
                        icon: <RemoveIcon fontSize="small" />,
                        label: "Remove permanently",
                        danger: true,
                        onClick: () => onRemove(row),
                      });
                    }
                    return (
                      <TableRow key={row?.id} hover sx={{ "&:hover": { background: TOKENS.bgSunken } }}>
                        <DataCell>
                          <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Monogram name={name} size={36} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 600,
                                  color: TOKENS.fg1,
                                }}
                              >
                                {name}
                              </Typography>
                              <IdMono value={row?.id} />
                            </Box>
                          </Stack>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg1, wordBreak: "break-all" }}>
                            {row?.host_name || "—"}
                          </Typography>
                        </DataCell>
                        <DataCell>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: TOKENS.fg1,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            }}
                          >
                            {row?.edge_ip || "—"}
                          </Typography>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>{row?.res_code ?? "—"}</Typography>
                        </DataCell>
                        <DataCell>
                          <ReachableDot value={row?.isActive} />
                        </DataCell>
                        <DataCell>
                          <StatusPill status={row?.isDeleted ? "Suspended" : "Active"} />
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                            {row?.created_at ? formatDate(row.created_at) : "—"}
                          </Typography>
                        </DataCell>
                        <DataCell align="right">
                          <RowMenu actions={actions} />
                        </DataCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && sorted.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${TOKENS.borderSubtle}` }}>
            <TablePagination
              component="div"
              count={counts.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                "& .MuiTablePagination-toolbar": { minHeight: 48, fontSize: 12.5 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  color: TOKENS.fg2,
                  fontSize: 12.5,
                },
              }}
            />
          </Box>
        )}
      </Box>

      <RegionModal open={editOpen} handleClose={() => setEditOpen(false)} region={region} loading={loading} />
      <ViewRegionModal open={viewOpen} handleClose={() => setViewOpen(false)} region={region} loading={loading} />
      <DeleteRegionModal
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        id={regionID}
        name={regionName}
        removeFlag={removeFlag}
      />
      <RestoreRegionModal
        open={restoreOpen}
        handleClose={() => setRestoreOpen(false)}
        id={regionID}
        name={regionName}
      />
      <TestRegionModal open={testOpen} handleClose={() => setTestOpen(false)} region={region} loading={loading} />
    </Box>
  );
}

export default RegionTable;
